import axios, {
	AxiosError,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from 'axios';

const TOKEN_KEY = 'libro_admin_token';

// services-sam token error codes (util/errors.ts). A response carrying one of
// these means the session is invalid/expired and we must re-login.
const AUTH_ERROR_CODES = {
	TOKEN_NOT_FOUND: 2,
	INVALID_TOKEN: 3,
} as const;

const apiClient = axios.create({
	baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3000',
	timeout: 60_000,
	headers: {
		'Content-Type': 'application/json',
	},
});

apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		const token = localStorage.getItem(TOKEN_KEY);
		if (token) {
			// services-sam reads the raw `token` header (not Authorization).
			config.headers.token = token;
		}
		return config;
	},
	(error) => {
		return Promise.reject(error);
	},
);

apiClient.interceptors.response.use(
	(response: AxiosResponse) => {
		// services-sam envelope: { ok, data, error, errorCode }. Errors arrive
		// as HTTP 200 with ok === false.
		if (response.data?.ok === false) {
			const err = new Error(
				response.data.error || 'Error en la solicitud',
			) as Error & { isAuthError?: boolean };
			if (
				typeof response.data?.errorCode === 'number' &&
				Object.values(AUTH_ERROR_CODES).includes(
					response.data.errorCode,
				)
			) {
				err.isAuthError = true;
				localStorage.removeItem(TOKEN_KEY);
				if (window.location.pathname !== '/login') {
					window.location.href = '/login';
				}
			}
			return Promise.reject(err);
		}
		// Unwrap the envelope so callers keep reading response.data.<field>.
		if (response.data && typeof response.data === 'object' && 'data' in response.data) {
			response.data = response.data.data;
		}
		return response;
	},
	(error: AxiosError) => {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					localStorage.removeItem(TOKEN_KEY);
					if (
						window.location.pathname !== '/login'
					) {
						window.location.href = '/login';
					}
					break;
				case 404:
					console.error('Resource not found');
					break;
				case 500:
					console.error('Server error');
					break;
			}
		} else if (error.request) {
			console.error('No response from server');
		} else {
			console.error('Request error:', error.message);
		}
		return Promise.reject(error);
	},
);

export default apiClient;
